// Lettura ad alta voce: prima la voce del server (se configurata), poi quella dell'iPad.
let unlocked = false;
let sharedAudio: HTMLAudioElement | null = null;
let itVoice: SpeechSynthesisVoice | null = null;
// Risolutore della playUrl in corso: stopSpeaking() e una nuova playUrl lo chiamano,
// così nessuna promise resta appesa fino al tempo massimo.
let pendingResolve: (() => void) | null = null;

// Mezzo secondo di silenzio WAV: serve un src reale perché iOS "sblocchi" l'elemento.
const SILENT_WAV =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YQAAAAA=';

/** Da chiamare dentro un tocco dell'utente: sblocca l'audio su iOS. */
export function unlockAudio(): void {
  if (unlocked) return;
  unlocked = true;
  try {
    sharedAudio = new Audio(SILENT_WAV);
    void sharedAudio.play().catch(() => undefined);
  } catch { /* pazienza */ }
  try {
    speechSynthesis.getVoices();
  } catch { /* non supportato */ }
}

function pickItalianVoice(): SpeechSynthesisVoice | null {
  if (itVoice) return itVoice;
  const voices = speechSynthesis.getVoices().filter((v) => v.lang.toLowerCase().startsWith('it'));
  if (voices.length === 0) return null;
  // Preferisce le voci "enhanced/premium" se scaricate sull'iPad.
  itVoice =
    voices.find((v) => /enhanc|premium|migliorat/i.test(v.name)) ??
    voices.find((v) => /alice|federica|paola/i.test(v.name)) ??
    voices[0] ?? null;
  return itVoice;
}

function settlePending(): void {
  const r = pendingResolve;
  pendingResolve = null;
  r?.();
}

export function stopSpeaking(): void {
  clearQueue();
  try { speechSynthesis.cancel(); } catch { /* ok */ }
  if (sharedAudio) {
    sharedAudio.pause();
    sharedAudio.currentTime = 0;
  }
  // pause() non genera "ended": la promise di playUrl va chiusa a mano.
  settlePending();
}

/** Com'è andata una riproduzione: arrivata alla fine, fermata da noi, o muta (errore, tempo
 *  scaduto, finita molto prima della sua durata — su iPad succede quando si apre il microfono). */
export type EsitoAudio = 'sentito' | 'fermato' | 'muto';

/** Riproduce un file audio del server (url) riusando l'elemento sbloccato.
 *  Lancia se play() viene rifiutato; se parte ma non si sente risolve 'muto' (i chiamanti, in
 *  entrambi i casi, ripiegano sulla voce locale). */
export async function playUrl(url: string): Promise<EsitoAudio> {
  return playUrlEsito(url);
}

async function playUrlEsito(url: string): Promise<EsitoAudio> {
  if (!sharedAudio) sharedAudio = new Audio();
  // Una riproduzione sostituita non resta appesa: chi aspettava la precedente viene liberato.
  settlePending();
  const a = sharedAudio;
  a.src = url;
  await a.play();
  return new Promise<EsitoAudio>((resolve) => {
    let done = false;
    let timer: number | undefined;
    const finish = (esito: EsitoAudio): void => {
      if (done) return;
      done = true;
      if (timer !== undefined) clearTimeout(timer);
      if (pendingResolve === fermato) pendingResolve = null;
      resolve(esito);
    };
    const fermato = (): void => finish('fermato');
    timer = window.setTimeout(() => finish('muto'), 5 * 60 * 1000);
    pendingResolve = fermato;
    a.onended = () => {
      // «ended» molto prima della durata vera: non si è sentito niente.
      const d = a.duration;
      finish(Number.isFinite(d) && d > 1 && a.currentTime < d * 0.5 ? 'muto' : 'sentito');
    };
    a.onerror = () => finish('muto');
  });
}

/* ---------- coda di riproduzione ----------
   Per la telefonata: le frasi arrivano una alla volta dal server e vanno lette in
   ordine, senza sovrapporsi. playQueue accoda; la promise restituita si risolve
   quando la coda è vuota. clearQueue svuota e ferma (usata da stopSpeaking). */
let queue: string[] = [];
let queueRun: Promise<void> | null = null;
let queueGen = 0;
// Quanti file della coda si sono sentiti davvero e quanti no (azzerati da contaVoce()).
let sentiti = 0;
let muti = 0;

/** Restituisce e azzera il conto dei file sentiti/muti dall'ultima chiamata. */
export function contaVoce(): { sentiti: number; muti: number } {
  const c = { sentiti, muti };
  sentiti = 0;
  muti = 0;
  return c;
}

export function playQueue(url: string): Promise<void> {
  queue.push(url);
  if (!queueRun) {
    const gen = queueGen;
    queueRun = (async () => {
      while (queue.length > 0 && gen === queueGen) {
        const next = queue.shift()!;
        try {
          const esito = await playUrlEsito(next);
          if (esito === 'sentito') sentiti++;
          else if (esito === 'muto') muti++;
        } catch { muti++; /* file mancante o play rifiutato: si passa al prossimo */ }
      }
      queueRun = null;
    })();
  }
  return queueRun;
}

/** Si risolve quando la coda di riproduzione è vuota (subito se non c'è nulla). */
export function waitQueue(): Promise<void> {
  return queueRun ?? Promise.resolve();
}

export function clearQueue(): void {
  queue = [];
  queueGen++;
}

/** Legge un testo con la voce del dispositivo. Non resta mai appesa: ha un tempo massimo. */
export function speakLocal(text: string): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };
    // iOS a volte non emette onend: stima generosa (400 ms a parola) + margine.
    const maxMs = Math.max(8000, text.split(/\s+/).length * 400 + 3000);
    setTimeout(finish, maxMs);
    try {
      const utter = new SpeechSynthesisUtterance(text);
      utter.lang = 'it-IT';
      const voice = pickItalianVoice();
      if (voice) utter.voice = voice;
      utter.rate = 0.95;
      utter.onend = finish;
      utter.onerror = finish;
      speechSynthesis.speak(utter);
    } catch {
      finish();
    }
  });
}
